import { setSession } from 'src/utils/auth/utils';
import { localStorageGetItem } from 'src/utils/local-storage';

import { safeAxiosInstance } from 'src/services/axios';

import { rootApi } from '../rootApi';
import { APIResponse, AuthResponse, HTTPRequestType } from '../api.types';
import {
  LoginDTO,
  AuthContext,
  CreateAccountDTO,
  RegisterInviteDTO,
  ActivationPreview,
  RegisterFromIntentDTO,
  CompletePasswordResetDTO,
  SetActivationPasswordDTO,
  ActivationSubscribeResponse,
  RequestEmailVerificationDTO,
  AddActivationPaymentMethodDTO
} from './auth.types';

const AUTH_CONTEXT_RETRIES = 2;
const AUTH_CONTEXT_RETRY_DELAY_MS = 300;

const delay = (ms: number) => new Promise((resolve) => { setTimeout(resolve, ms); });

export const authApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    requestEmailVerification: builder.mutation<APIResponse, RequestEmailVerificationDTO>({
      query: (data: RequestEmailVerificationDTO) => ({
        url: '/auth/request-verification',
        method: HTTPRequestType.POST,
        data,
      }),
    }),
    createAccount: builder.mutation<AuthResponse, CreateAccountDTO>({
      query: (data: CreateAccountDTO) => ({
        url: '/auth/register',
        method: HTTPRequestType.POST,
        data,
      }),
      transformResponse: (response: AuthResponse) => {
        setSession(response.access_token);

        return response;
      },
    }),
    registerFromIntent: builder.mutation<AuthResponse, RegisterFromIntentDTO>({
      query: (data: RegisterFromIntentDTO) => ({
        url: '/auth/register-from-intent',
        method: HTTPRequestType.POST,
        data,
      }),
      transformResponse: (response: AuthResponse) => {
        setSession(response.access_token);

        return response;
      },
    }),

    verifyEmail: builder.mutation<APIResponse, string>({
      query: (code: string) => ({
        url: '/auth/verify-account',
        method: HTTPRequestType.POST,
        data: {
          code,
        },
      }),
    }),
    getAuthContext: builder.query<AuthContext, void>({
      // JT1-1643: /auth/me decides whether the app renders or the user is sent to login, so a
      // single transient failure used to end a working session. Retry a server-side failure a
      // bounded number of times before reporting it. A 4xx is the server's considered answer and
      // is never retried; the bound is what keeps the JT1-1509 request storm from returning.
      async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
        let lastError: any;

        for (let attempt = 0; attempt <= AUTH_CONTEXT_RETRIES; attempt += 1) {
          // eslint-disable-next-line no-await-in-loop
          const result = await baseQuery({ url: '/auth/me' });

          if (!result.error) {
            return { data: result.data as AuthContext };
          }

          lastError = result.error;

          const status = (result.error as { status?: number }).status;
          const worthRetrying = status === undefined || status >= 500;

          if (!worthRetrying) break;

          // eslint-disable-next-line no-await-in-loop
          await delay(AUTH_CONTEXT_RETRY_DELAY_MS * (attempt + 1));
        }

        return { error: lastError };
      },
      providesTags: ['AuthContext']
    }),

    checkAuthStatus: builder.query<AuthContext, void>({
      async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {

        const token = localStorageGetItem('access_token', undefined);

        // JT1-1643: without a token this request can only ever come back 401, and that 401 is then
        // read as "the session ended" — so a cleared session kept re-proving itself dead. Answer
        // locally instead of asking.
        if (!token) {
          return { error: { status: 401, data: { message: 'no session token' } } };
        }

        try {
          const response = await safeAxiosInstance.get('/auth/me', {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          return { data: response.data };
        } catch (error) {
          return {
            error: {
              status: error.response?.status,
              data: error.response?.data
            }
          };
        }
      },
      providesTags: ['AuthContext'],
    }),

    login: builder.mutation<AuthResponse, LoginDTO>({
      query: (data: LoginDTO) => ({
        url: '/auth/login',
        method: HTTPRequestType.POST,
        data,
      }),
      transformResponse: (response: AuthResponse) => {
        setSession(response.access_token);

        return response;
      },
    }),
    loginExternal: builder.mutation<AuthResponse, LoginDTO>({
      query: (data: LoginDTO) => ({
        url: '/auth/login-external',
        method: HTTPRequestType.POST,
        data,
      }),
      transformResponse: (response: AuthResponse) => {
        setSession(response.access_token);

        return response;
      },
    }),
    requestResetPassword: builder.mutation<APIResponse, RequestEmailVerificationDTO>({
      query: (data: RequestEmailVerificationDTO) => ({
        url: '/auth/password-reset/request',
        method: HTTPRequestType.POST,
        data,
      }),
    }),
    completePasswordReset: builder.mutation<APIResponse, CompletePasswordResetDTO>({
      query: (data: CompletePasswordResetDTO) => ({
        url: '/auth/password-reset/complete',
        method: HTTPRequestType.POST,
        data,
      }),
    }),
    // ---- gated agency/employer activation link ----

    getActivationPreview: builder.query<ActivationPreview, string>({
      query: (token: string) => ({ url: `/auth/activation/${token}` }),
    }),
    setActivationPassword: builder.mutation<AuthResponse, SetActivationPasswordDTO>({
      query: (data: SetActivationPasswordDTO) => ({
        url: '/auth/activation/set-password',
        method: HTTPRequestType.POST,
        data,
      }),
      transformResponse: (response: AuthResponse) => {
        setSession(response.access_token);

        return response;
      },
    }),
    exchangeActivationToken: builder.mutation<AuthResponse, string>({
      query: (token: string) => ({
        url: '/auth/activation/exchange',
        method: HTTPRequestType.POST,
        data: { token },
      }),
      transformResponse: (response: AuthResponse) => {
        setSession(response.access_token);

        return response;
      },
    }),
    // payment for a pending tenant - token-authenticated, because the tenant billing endpoints only
    // admit active organizations and a gated tenant stays pending until it has paid
    createActivationSetupIntent: builder.mutation<{ client_secret: string }, string>({
      query: (token: string) => ({
        url: '/auth/activation/setup-intent',
        method: HTTPRequestType.POST,
        data: { token },
      }),
    }),
    addActivationPaymentMethod: builder.mutation<APIResponse, AddActivationPaymentMethodDTO>({
      query: (data: AddActivationPaymentMethodDTO) => ({
        url: '/auth/activation/payment-method',
        method: HTTPRequestType.POST,
        data,
      }),
    }),
    subscribeActivation: builder.mutation<ActivationSubscribeResponse, string>({
      query: (token: string) => ({
        url: '/auth/activation/subscribe',
        method: HTTPRequestType.POST,
        data: { token },
      }),
    }),

    acceptInvite: builder.mutation<AuthResponse, RegisterInviteDTO>({
      query: (data) => ({
        url: '/auth/register-from-invite',
        method: HTTPRequestType.POST,
        data,
      }),
      transformResponse: (response: AuthResponse) => {
        setSession(response.access_token);

        return response;
      },
    }),
  }),
});

export const {
  useRequestEmailVerificationMutation,
  useCreateAccountMutation,
  useRegisterFromIntentMutation,
  useVerifyEmailMutation,
  useGetAuthContextQuery,
  useLazyGetAuthContextQuery,
  useCheckAuthStatusQuery,
  useLoginMutation,
  useLoginExternalMutation,
  useRequestResetPasswordMutation,
  useCompletePasswordResetMutation,
  useAcceptInviteMutation,
  useGetActivationPreviewQuery,
  useSetActivationPasswordMutation,
  useExchangeActivationTokenMutation,
  useCreateActivationSetupIntentMutation,
  useAddActivationPaymentMethodMutation,
  useSubscribeActivationMutation,
} = authApi;
